import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/layout/Layout";

import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import ApplyLeave from "@/pages/ApplyLeave";
import LeaveHistory from "@/pages/LeaveHistory";
import LeaveBalance from "@/pages/LeaveBalance";
import LeaveApprovals from "@/pages/LeaveApprovals";
import LeaveConfiguration from "@/pages/LeaveConfiguration";
import HolidayCalendar from "@/pages/HolidayCalendar";
import PunchInOut from "@/pages/PunchInOut";
import AttendanceHistory from "@/pages/AttendanceHistory";
import AttendanceAdmin from "@/pages/AttendanceAdmin";
import Employees from "@/pages/Employees";
import EmployeeProfile from "@/pages/EmployeeProfile";
import SalarySlips from "@/pages/SalarySlips";
import SalarySlipGenerator from "@/pages/SalarySlipGenerator";
import SalaryCertificates from "@/pages/SalaryCertificates";
import WpsPayroll from "@/pages/WpsPayroll";
import DocumentExpiry from "@/pages/DocumentExpiry";
import Reports from "@/pages/Reports";
import EmailNotifications from "@/pages/EmailNotifications";
import Settings from "@/pages/Settings";

const STAFF = ["admin", "manager"];
const ADMIN = ["admin"];
const EMPLOYEE = ["employee"];

function ByRole({ employee, staff }) {
  const { user } = useAuth();
  return user.role === "employee" ? employee : staff;
}

const guard = (roles, element) => <ProtectedRoute roles={roles}>{element}</ProtectedRoute>;

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors toastOptions={{ style: { borderRadius: 14 } }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<ByRole employee={<EmployeeDashboard />} staff={<AdminDashboard />} />} />
            <Route path="/attendance" element={<ByRole employee={<PunchInOut />} staff={<AttendanceAdmin />} />} />
            <Route path="/attendance/punch" element={guard(EMPLOYEE, <PunchInOut />)} />
            <Route path="/attendance/history" element={guard(EMPLOYEE, <AttendanceHistory />)} />

            <Route path="/leave/apply" element={guard(EMPLOYEE, <ApplyLeave />)} />
            <Route path="/leave/history" element={guard(EMPLOYEE, <LeaveHistory />)} />
            <Route path="/leave/balance" element={guard(EMPLOYEE, <LeaveBalance />)} />
            <Route path="/leave/approvals" element={guard(STAFF, <LeaveApprovals />)} />
            <Route path="/leave/configuration" element={guard(ADMIN, <LeaveConfiguration />)} />

            <Route path="/holidays" element={<HolidayCalendar />} />

            <Route path="/employees" element={guard(STAFF, <Employees />)} />
            <Route path="/employees/:id" element={guard(STAFF, <EmployeeProfile />)} />
            <Route path="/profile" element={<EmployeeProfile selfMode />} />

            <Route path="/payroll/slips" element={<SalarySlips />} />
            <Route path="/payroll/slips/generate" element={guard(ADMIN, <SalarySlipGenerator />)} />
            <Route path="/payroll/certificates" element={<SalaryCertificates />} />
            <Route path="/payroll/wps" element={guard(ADMIN, <WpsPayroll />)} />

            <Route path="/document-expiry" element={guard(ADMIN, <DocumentExpiry />)} />
            <Route path="/notifications" element={guard(STAFF, <EmailNotifications />)} />
            <Route path="/reports" element={guard(ADMIN, <Reports />)} />
            <Route path="/settings" element={<Settings />} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
